import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import { ResponseProps } from 'services/services.types';

import { RootState } from 'store';
import { setUserState } from 'store/signup/signupSlice';
import { clearUser, setToken } from 'store/user/userSlice';

import { notifyToast } from 'shared/Toast/Toast';

import packageInfo from '../../package.json';

import { PathName } from 'router/paths';
import { withBasePath } from './basePath';
import { session, SESSION_KEYS } from './storage';
import { clearWorkOSCookies, hasWorkOSOverride, hasWorkOSSession } from './workos';

interface IRes {
  data: { data: { accessToken: string; refreshToken: string } };
  error?: {
    data: {
      errorMessage: number;
      message: string;
    };
    status: number;
  };
}

/**
 * Shared refresh request promise to avoid parallel token-renewal calls.
 */
let refreshTokenPromise: Promise<IRes | null> | null = null;

/**
 * Executes a token renewal request using the provided refresh token.
 * @param refreshTokenValue - Refresh token used to obtain a new access token.
 * @param api - RTK Query base query API instance.
 * @param extraOptions - Extra RTK Query options.
 * @returns The refresh response payload or null when no data is returned.
 */
const requestTokenRefresh = async (
  refreshTokenValue: string,
  api: BaseQueryApi,
  extraOptions: {}
): Promise<IRes | null> => {
  const refreshResult = await baseQuery(
    {
      body: { refreshToken: refreshTokenValue },
      headers: {
        'API-KEY': import.meta.env.VITE_API_KEY
      },
      method: 'POST',
      url: '/auth/token-renewal'
    },
    api,
    extraOptions
  );

  if (refreshResult.error) {
    return null;
  }

  return refreshResult as IRes;
};

const createEmptyResponseError = (): { error: FetchBaseQueryError } => ({
  error: {
    data: { errorCode: 1, message: 'Empty response body' },
    error: 'Empty response body',
    status: 'CUSTOM_ERROR'
  }
});

/**
 * Raw fetchBaseQuery instance with default headers and URL handling.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BASE_URL + '/',
  credentials: 'include',
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(`${key}[]`, String(item)));
      } else if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    });
    return searchParams.toString();
  },
  prepareHeaders: (headers, { getState }) => {
    const staffId = session.get(SESSION_KEYS.STAFF_ID);
    headers.set('App-Version', packageInfo.version);
    headers.set('Platform', 'web-patient');
    headers.set('API-KEY', import.meta.env.VITE_API_KEY ?? '');
    staffId && headers.set('impersonation-mode-by', staffId);

    // Skip Authorization header when using WorkOS cookie sessions
    if (!hasWorkOSSession()) {
      const token = headers.get('Authorization') ?? (getState() as RootState).user.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
    }

    if (hasWorkOSOverride()) {
      headers.set('X-WorkOS-Override', 'true');
    }

    return headers;
  }
});

/**
 * Base query with null-body guard. When the API returns null/empty body,
 * fetchBaseQuery treats it as success ({ data: null }). This wrapper converts
 * that to an error so transformResponse is never called with null (LMD-25490).
 */
export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (!result.error && (result.data === null || result.data === undefined)) {
    const httpStatus = result.meta?.response?.status;
    if (httpStatus === 204 || httpStatus === 205) {
      return result;
    }
    return createEmptyResponseError();
  }
  return result;
};

/**
 * Base RTK Query implementation that refreshes expired tokens and retries once.
 * @param args - RTK Query fetch arguments.
 * @param api - RTK Query base query API instance.
 * @param extraOptions - Extra RTK Query options.
 * @returns API response result with optional re-authentication.
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  const store = api.getState() as RootState;
  const { refreshToken } = store.user;
  const { user } = (api.getState() as RootState).orchestrate;

  const {
    user: { refreshToken: guestUserRefreshToken }
  } = store.signUp;

  if (result.error && result.error.status === 401) {
    // WorkOS cookie-based session: retry once (middleware may have refreshed the cookie)
    if (hasWorkOSSession()) {
      result = await baseQuery(args, api, extraOptions);
      if (result.error?.status === 401) {
        // Session truly expired — clear client-side state
        api.dispatch(clearUser());
        clearWorkOSCookies();
        window.location.href = withBasePath(PathName.Login);
      }
      return result;
    }

    if (!refreshToken && !guestUserRefreshToken) {
      // if this is orchestrate flow, we don't need to clear user, we need to regenerate the token. To do that we move user back so he can regenerate the token
      if (!!user?.accessToken) {
        window.history.back();
      } else {
        notifyToast('Session has expired, please log in again');
        api.dispatch(clearUser());
        window.location.href = withBasePath(PathName.Login);
      }
      return result;
    }

    // try to get a new token
    let pendingRefresh: Promise<IRes | null> | null = null;
    try {
      const refreshTokenValue = refreshToken || guestUserRefreshToken;
      if (!refreshTokenPromise) {
        refreshTokenPromise = requestTokenRefresh(refreshTokenValue, api, extraOptions);
      }
      pendingRefresh = refreshTokenPromise;
      const refreshResult = await pendingRefresh;

      if (!refreshResult || refreshResult?.error) {
        api.dispatch(clearUser());
        throw new Error('Error while getting refresh token');
      }

      const { data } = refreshResult.data;

      if (data) {
        // if we are not logged in and have only guest access token
        if (guestUserRefreshToken) {
          api.dispatch(setUserState(data));
        } else {
          // else log user in
          api.dispatch(setToken(data));
        }

        // retry the initial query
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(clearUser());
      }
    } catch (e) {
      // log out user if we can't get a new token
      api.dispatch(clearUser());
      throw e;
    } finally {
      // Clear the promise after use to allow future refresh attempts
      if (refreshTokenPromise === pendingRefresh) {
        refreshTokenPromise = null;
      }
    }
  }

  return result;
};

type PaginatedResponse<Item> = ResponseProps<Item[], unknown>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type FetchAllPagesOptions<Item, Response extends PaginatedResponse<Item>> = {
  api: BaseQueryApi;
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;
  extraOptions: object;
  limit?: number;
  pageNo?: number;
  params?: Record<string, unknown>;
  url: string;
};

const extractItemId = (item: unknown): string | undefined => {
  if (item && typeof item === 'object') {
    const record = item as Record<string, unknown>;
    if (typeof record._id === 'string') return record._id;
    if (typeof record.id === 'string') return record.id;
  }
  return undefined;
};

const extractTotalCount = (info: unknown): number | undefined => {
  if (info && typeof info === 'object' && 'totalCount' in info) {
    return (info as { totalCount?: number }).totalCount;
  }
  return undefined;
};

export const fetchAllPages = async <Item, Response extends PaginatedResponse<Item>>(
  options: FetchAllPagesOptions<Item, Response>
): Promise<{ data: Response } | { error: FetchBaseQueryError }> => {
  const { api, baseQuery, extraOptions, limit = 20, pageNo = 0, params, url } = options;

  const createEmptyResponse = (): Response =>
    ({
      data: [],
      errorCode: 0,
      info: {} as Response['info'],
      message: ''
    }) as unknown as Response;

  if (limit <= 0) {
    return { data: createEmptyResponse() };
  }

  const items: Item[] = [];
  const seenIds = new Set<string>();
  let currentPage = pageNo;
  let lastResponse: Response | undefined;
  let latestTotalCount: number | undefined;

  while (true) {
    const request: FetchArgs = { params: { ...params, limit, pageNo: currentPage }, url };

    const result = await baseQuery(request as string | FetchArgs, api, extraOptions);

    if (result.error) {
      return { error: result.error };
    }

    const payload = result.data as Response | undefined;
    if (!payload) break;

    lastResponse = payload;
    const pageItems = payload.data ?? [];
    const beforeLength = items.length;

    for (const item of pageItems) {
      const itemId = extractItemId(item);

      if (itemId) {
        if (!seenIds.has(itemId)) {
          seenIds.add(itemId);
          items.push(item);
        }
      } else {
        items.push(item);
      }
    }

    const totalFromInfo = extractTotalCount(payload.info);
    if (totalFromInfo !== undefined) {
      latestTotalCount = totalFromInfo;
    }

    const noNewItems = items.length === beforeLength;
    const reachedTotal = latestTotalCount !== undefined && items.length >= latestTotalCount;
    const isLastPage = !pageItems.length || pageItems.length < limit || reachedTotal || noNewItems;

    if (isLastPage) {
      break;
    }

    currentPage += 1;
  }

  const baseResponse = lastResponse ?? createEmptyResponse();
  const totalCount = Math.max(latestTotalCount ?? 0, items.length);

  const mergedInfo =
    baseResponse.info && typeof baseResponse.info === 'object'
      ? { ...(baseResponse.info as Record<string, unknown>), totalCount }
      : { totalCount };

  return {
    data: {
      ...baseResponse,
      data: items,
      info: mergedInfo as Response['info']
    }
  };
};
